import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Table,
  Tag,
  Descriptions,
  Modal,
  Progress,
  message, 
  Tooltip,
  Empty
} from 'antd';
import {
  BookOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  LaptopOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  EditOutlined,
  SwapOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const ClassReport = () => {
const [dateRange, setDateRange] = useState([
  dayjs().startOf('year'), 
  dayjs().endOf('year')    
]);

  
  const [summaryData, setSummaryData] = useState(null);
  const [changesData, setChangesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedChange, setSelectedChange] = useState(null);
  const [changeModalVisible, setChangeModalVisible] = useState(false);

  // Real API calls using fetch
  const fetchSummaryData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/class-reports/summary?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch class summary');
    return await response.json();
  };

  const fetchChangesData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/class-reports/changes?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch class changes');
    return await response.json();
  };

const loadData = async () => {
  if (!API_URL) {
    setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
    return;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    setError('Authentication token not found. Please log in again.');
    return;
  }

  setLoading(true);
  setError(null);
  
  try {
    const [summary, changes] = await Promise.all([
      fetchSummaryData(),
      fetchChangesData()
    ]);

    // Transform API response for charts
    const subjectsData = (summary.most_taught_subjects || []).map(item => ({
      name: item.subject_name,
      count: item.total_classes
    }));

    const platformDist = (summary.platform_distribution || []).map(item => ({
      name: item.platform,
      count: item.count,
      percentage: item.percentage
    }));

    setSummaryData(summary);          // keep raw for statistics cards
    setChangesData(changes);
    
    message.success('Class report data loaded successfully');
  } catch (err) {
    console.error('Error loading class report data:', err);
    
    let errorMessage = 'Failed to load class report data';
    if (err.status) {
      if (err.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.status === 400) {
        errorMessage = 'Invalid request parameters';
      } else {
        errorMessage = `Server error: ${err.status}`;
      }
    } else {
      errorMessage = err.message || 'An unexpected error occurred';
    }
    
    setError(errorMessage);
    message.error(errorMessage);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('class-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Class Analytics Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  const showChangeDetails = (record) => {
    setSelectedChange(record);
    setChangeModalVisible(true);
  };

  // Prepare chart data
const subjectsChartData = summaryData?.top_subjects_by_hours?.map(subject => ({
  name: subject.subject_name?.length > 20 
    ? subject.subject_name.substring(0, 20) + '...' 
    : subject.subject_name,
  count: subject.total_hours || 0
})) || [];


  const platformData = summaryData?.platform_distribution?.map(platform => ({
    name: platform.platform || 'Unknown',
    count: parseInt(platform.count) || 0,
    percentage: parseFloat(platform.percentage) || 0
  })) || [];

  // Platform colors mapping
  const platformColors = {
    'online': '#1890ff',
    'onsite': '#52c41a', 
    'athome': '#fa8c16',
    'Unknown': '#8c8c8c'
  };

  // Platform icons mapping
  const platformIcons = {
    'online': <LaptopOutlined />,
    'onsite': <EnvironmentOutlined />,
    'athome': <HomeOutlined />,
    'Unknown': <InfoCircleOutlined />
  };

  // Table columns for class changes
  const changesColumns = [
    {
      title: 'Class Code',
      dataIndex: 'class_code',
      key: 'class_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Subject',
      dataIndex: 'subject_name',
      key: 'subject_name',
      render: (subject) => (
        <Space>
          <BookOutlined />
          {subject}
        </Space>
      )
    },
    {
      title: 'Teacher',
      dataIndex: 'teacher_name',
      key: 'teacher_name'
    },
    {
      title: 'Original Date',
      dataIndex: 'originaldate',
      key: 'originaldate',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    },
    {
      title: 'New Date',
      dataIndex: 'newdate',
      key: 'newdate',
      render: (date) => (
        <Text strong style={{ color: '#f5222d' }}>
          {dayjs(date).format('MMM DD, YYYY')}
        </Text>
      )
    },
    {
      title: 'Reason',
      dataIndex: 'changereason',
      key: 'changereason',
      render: (reason) => (
        <Tag color="orange">{reason || 'Not specified'}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          size="small" 
          icon={<InfoCircleOutlined />}
          onClick={() => showChangeDetails(record)}
        >
          Details
        </Button>
      )
    }
  ];


  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value} (${payload[0].payload.percentage}%)`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="class-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Class Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Class Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of class distribution, platforms, and schedule changes
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange as any}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button
                type="default"
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!summaryData && !changesData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Classes"
                  value={summaryData?.total_classes || 0}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Hours"
                  value={
                    summaryData?.total_hours
                      ? Number(summaryData.total_hours).toFixed(2)
                      : 0
                  }
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>

            </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Taught Hours"
                    value={summaryData?.taught_hours || 0}
                    suffix="hrs"
                    prefix={<BookOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Untaught Hours"
                    value={summaryData?.untaught_hours || 0}
                    suffix="hrs"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#fa541c' }}
                  />
                </Card>
              </Col>
          </Row>

 
{/* Charts Row */}
<Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
  {/* Most Taught Subjects */}
  <Col xs={24} lg={12}>

<Card 
  title={
    <Space>
      <BookOutlined />
      Most Taught Subjects
    </Space>
  }
>
  {subjectsChartData.length > 0 ? (
    <BarChart width={500} height={350} data={subjectsChartData.slice(0, 10)}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis 
        dataKey="name" 
        angle={-45}
        textAnchor="end"
        height={80}
        interval={0}
        fontSize={12}
      />
      <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
      <Tooltip
        {...{
          formatter: (value: any, name: any) => [`${value} hours`, 'Total Hours'],
          labelFormatter: (label: any) => `Subject: ${label}`
        } as any}
      />
      <Bar 
        dataKey="count" 
        fill="#8884d8" 
        label={{ position: 'top', fontSize: 12 }}
      />
    </BarChart>
  ) : (
    <Empty description="No subject data available" />
  )}
</Card>
  </Col>

  {/* Platform Distribution */}
  <Col xs={24} lg={12}>
    <Card 
      title={
        <Space>
          <EnvironmentOutlined style={{ color: '#52c41a' }} />
          Platform Distribution
        </Space>
      }
    >
      {platformData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={platformData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) => `${name} (${percentage}%)`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {platformData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={platformColors[entry.name] || '#8c8c8c'} 
                  />
                ))}
              </Pie>
              <RechartsTooltip content={<PieTooltip active={undefined} payload={undefined} />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Space size="large" wrap>
              {platformData.map((entry) => (
                <Space key={entry.name}>
                  {platformIcons[entry.name]}
                  <div 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: platformColors[entry.name] || '#8c8c8c',
                      borderRadius: '50%' 
                    }}
                  />
                  <Text>{entry.name}: {entry.count} ({entry.percentage}%)</Text>
                </Space>
              ))}
            </Space>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Text type="secondary">No platform data available</Text>
        </div>
      )}
    </Card>
  </Col>
</Row>


          {/* Platform Progress Bars */}
          {platformData.length > 0 && (
            <Card title="Platform Usage Breakdown" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                {platformData.map((platform) => (
                  <Col xs={24} sm={12} lg={8} key={platform.name}>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Space>
                          {platformIcons[platform.name]}
                          <Text strong>{platform.name}</Text>
                        </Space>
                        <Text>{platform.count} classes</Text>
                      </div>
                      <Progress 
                        percent={platform.percentage} 
                        strokeColor={platformColors[platform.name] || '#8c8c8c'}
                        format={(percent) => `${percent}%`}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* Class Changes Table */}
          <Card 
            title={
              <Space>
                <SwapOutlined style={{ color: '#fa8c16' }} />
                Class Schedule Changes
              </Space>
            }
          >
            {changesData?.length > 0 ? (
              <Table
                dataSource={changesData}
                columns={changesColumns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">No class changes recorded in this period</Text>
              </div>
            )}
          </Card>
        </Spin>

        {/* Change Details Modal */}
        <Modal
          title={
            <Space>
              <EditOutlined />
              Class Change Details
            </Space>
          }
          open={changeModalVisible}
          onCancel={() => setChangeModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setChangeModalVisible(false)}>
              Close
            </Button>
          ]}
          width={800}
        >
          {selectedChange && (
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Class Code" span={2}>
                <Tag color="blue">{selectedChange.class_code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Subject">
                {selectedChange.subject_name}
              </Descriptions.Item>
              <Descriptions.Item label="Teacher">
                {selectedChange.teacher_name} ({selectedChange.nickname})
              </Descriptions.Item>
              <Descriptions.Item label="Original Date">
                {dayjs(selectedChange.originaldate).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="New Date">
                <Text strong style={{ color: '#f5222d' }}>
                  {dayjs(selectedChange.newdate).format('MMMM DD, YYYY')}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Original Time">
                {selectedChange.originalstarttime} - {selectedChange.originalendtime}
              </Descriptions.Item>
              <Descriptions.Item label="New Time">
                <Text strong style={{ color: '#f5222d' }}>
                  {selectedChange.newstarttime} - {selectedChange.newendtime}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Change Reason" span={2}>
                <Tag color="orange">{selectedChange.changereason || 'Not specified'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>
                {selectedChange.note || 'No additional notes'}
              </Descriptions.Item>
              <Descriptions.Item label="Updated By">
                {selectedChange.updateby}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {dayjs(selectedChange.updated_at).format('MMMM DD, YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>

        <style {...{jsx: "true"} as any}>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};


export default withRoleAccess(ClassReport, ['superadmin']);